import {TrainingAPI} from "../../api/TrainingAPI";
import {useDispatch, useSelector} from "react-redux";
import {Badge, Button, Form, Input, Modal, Popconfirm, Row, Table, Tag} from "antd";
import React, {useEffect, useMemo, useState} from "react";
import {DeleteOutlined, EditOutlined, ExclamationCircleOutlined, MailOutlined} from "@ant-design/icons";
import {DeletePopConfirmContent} from "./PlayerOnboarding/PlayerTable";
import {
  addTeamCoach,
  deleteCoach,
  sendWelcomeEmailToCoach,
  sendWelcomeEmailToPlayer
} from "../../store/actions/team.actions";


const MODAL_MODE_EDIT = "edit";
const MODAL_MODE_CREATE = "create";

const MODAL_TITLES = {
  [MODAL_MODE_CREATE]: "Add Coach",
  [MODAL_MODE_EDIT]: "Edit Coach",
};


const CoachForm = ({
  defaultValue = {},
  onSubmit = () => {},
  onCancel = () => {},
}) => {
  const [form] = Form.useForm();
  const {
    info: {
      coach_list: coachList
    }
  } = useSelector(({ team }) => team);
  const [_, setPending] = useState(false);

  const handleSubmit = async () => {
    setPending(true);
    try {
      const values = await form.validateFields();
      await onSubmit({ ...defaultValue, ...values });
      handleCancel();
    } catch (e) {
      console.log(e);
      return;
    }
    setPending(false);
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  useEffect(() => {
    form.resetFields(["first_name", "Hello"]);
  }, []);

  const checkFullName = (getFieldValue) => {
    const firstName = getFieldValue("first_name")?.trim() || "";
    const lastName = getFieldValue("last_name")?.trim() || "";
    const foundCoaches = coachList.filter(
      ({ first_name, last_name, email }) => {
        return (
          first_name === firstName &&
          last_name === lastName &&
          email !== defaultValue?.email
        );
      }
    );
    return foundCoaches.length === 0;
  };

  const checkEmail = (getFieldValue) => {
    const email = getFieldValue("email")?.trim() || "";
    const foundCoaches = coachList.filter(
      ({ email: coachEmail, player_id }) => {
        return coachEmail === email && player_id !== defaultValue?.player_id;
      }
    );
    return foundCoaches.length === 0;
  };

  const nameValidator = ({ getFieldValue }) => ({
    validator(_, value) {
      if (checkFullName(getFieldValue)) {
        return Promise.resolve();
      }
      return Promise.reject(new Error("Same name already exists"));
    },
  });

  const emailValidator = ({ getFieldValue }) => ({
    validator(_, value) {
      if (checkEmail(getFieldValue)) {
        return Promise.resolve();
      }
      return Promise.reject(new Error("Same email already exists"));
    },
  });

  useEffect(() => {
    if (defaultValue === null) form.resetFields();
    else {
      form.setFieldsValue(defaultValue);
      form.validateFields();
    }
  }, [defaultValue]);

  return (
    <Form
      form={form}
      name="player-form"
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 18 }}
    >
      <Form.Item
        label="First Name"
        name="first_name"
        rules={[
          { required: true, message: "Please input first name" },
          nameValidator,
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Last Name"
        name="last_name"
        rules={[
          { required: true, message: "Please input last name" },
          nameValidator,
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Email"
        name="email"
        rules={[
          { required: true, type: "email", message: "Email is not a valid email" },
          emailValidator,
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item wrapperCol={{ offset: 16, span: 8 }}>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          type="primary"
          style={{ margin: "0 8px" }}
        >
          Save
        </Button>
      </Form.Item>
    </Form>
  );
}


export const CoachDialog = ({
  open,
  onCancel,
  selectedCoach = null,
  onSubmit,
}) => {
  const mode = useMemo(
    () => (selectedCoach ? MODAL_MODE_EDIT : MODAL_MODE_CREATE),
    [selectedCoach]
  );

  const modalTitle = useMemo(() => MODAL_TITLES[mode], [mode]);

  return (
    <Modal
      open={open}
      title={modalTitle}
      onCancel={onCancel}
      footer={null}
    >
      <CoachForm
        defaultValue={selectedCoach}
        onSubmit={onSubmit}
        onCancel={onCancel}
      />
    </Modal>
  );
};


const DeleteCoachPopConfirmContent = () => {
  return (
    <div style={{ width: 256 }}>
      This is a permanent action. <br />
      You are about to delete this coach's data and login credentials.
      <br />
    </div>
  );
};


export const CoachOnboarding = () => {
  const trainingAPI = new TrainingAPI();
  const dispatch = useDispatch();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);

  const {
    info: {
      coach_list: coachList,
      id: teamId
    }
  } = useSelector(({ team }) => team);

  const handleOpenAddModal = () => {
    setCreateModalOpen(true);
  };

  const handleOpenEditModal = (coach) => () => {
    setSelectedCoach(coach);
    setEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setCreateModalOpen(false);
    setEditModalOpen(false);
  };

  const addCoach = (newCoach) => {
    dispatch(
      addTeamCoach({
        ...newCoach,
        team_id: teamId
      })
    );
  };

  const editCoach = (coachData) => {
    // dispatch(
    //   editTeamPlayer({
    //     ...playerData,
    //     name: `${playerData.first_name} ${playerData.last_name}`,
    //   })
    // );
    console.log("Editing: ", coachData);
  };

  const handleDeleteCoach = async (record) => {
    dispatch(
      deleteCoach(record.email)
    );
  }

  const handleSendWelcomeEmail = (record) => async () => {
    dispatch(
      sendWelcomeEmailToCoach(record.email)
    );
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: {
        compare: (a, b) => a.last_name.localeCompare(b.last_name),
        multiple: 1,
      },
      render: (name, data) => {
        return (
          <>
            {data.first_name} {data.last_name}
          </>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: {
        compare: (a, b) => a.email.localeCompare(b.email),
        multiple: 3,
      },
      render: (email, data) => {
        return (
          <>
            {data.email}
          </>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
          <Badge
            offset={[-5, 5]}
          >
            <Button
              icon={<MailOutlined />}
              shape="circle"
              onClick={handleSendWelcomeEmail(record)}
              disabled={!record.email}
            />
          </Badge>
          <Popconfirm
            placement="right"
            title={<DeleteCoachPopConfirmContent />}
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleDeleteCoach(record)}
            icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
            // disabled={!hasRosterAccess}
          >
            <Button icon={<DeleteOutlined />} shape="circle" danger ghost/>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      <Row style={{ margin: "24px 0px 12px"}}>
        <Button type="primary" onClick={handleOpenAddModal} disabled={false}>
          Add Coach
        </Button>
      </Row>
      <Table
        rowKey="rowId"
        columns={columns}
        size="small"
        dataSource={coachList}
        style={{ width: 440, maxWidth: "100%" }}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "30"],
          position: ["topLeft"],
        }}
      />
      <CoachDialog
        open={createModalOpen}
        onCancel={handleCloseModal}
        onSubmit={addCoach}
        selectedCoach={null}
        // disabled={!hasRosterAccess}
      />
      {/*<CoachDialog*/}
      {/*  open={editModalOpen}*/}
      {/*  onCancel={handleCloseModal}*/}
      {/*  onSubmit={editCoach}*/}
      {/*  selectedCoach={selectedCoach}*/}
      {/*/>*/}
    </>
  )
};
